import type { StoreTheme } from '../../utils/storeTheme';

/**
 * Τα δεδομένα των κεντρικών (OrgDashboard) όπως τα επιστρέφει το /api/organizations — ίδια σχήματα με
 * πριν από τη νέα εμφάνιση (14/10/2026). Εδώ μόνο για να τα μοιράζονται οι καρτέλες του components/org.
 */

export interface OrgStore {
  id: number;
  name: string;
  display_name?: string | null;
  city?: string | null;
  username: string;
  address: string;
  phone_number: string;
  email: string;
  is_active: boolean;
  timezone: string;
  created_at: string;
  orders_today?: number;
  revenue_today?: number;
  /** Τιμοκατάλογος του καταστήματος (null = ο βασικός). */
  price_list_id?: number | null;
}

export interface OrgMember {
  id: number;
  role: string;
  created_at: string;
  org_user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  user_active: boolean;
  /** Εμβέλεια ανά μέλος: null/undefined = όλα τα καταστήματα, int[] = μόνο αυτά. */
  store_ids?: number[] | null;
}

export interface Organization {
  id: number;
  name: string;
  slug: string;
  logo_url: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  is_active: boolean;
  settings: any;
  /** Εμφάνιση: προεπιλογή όλων των καταστημάτων (migration 263). */
  theme?: StoreTheme | null;
  store_count?: number;
  member_count?: number;
}

export interface PriceListRef {
  id: number;
  name: string;
}

/** Αποτυχία φόρτωσης: 'forbidden' για 403 (άλλο μήνυμα, χωρίς «Δοκίμασε ξανά» που δεν θα βοηθούσε). */
export type LoadError = 'forbidden' | 'error';

export const loadErrorOf = (err: unknown): LoadError =>
  ((err as { response?: { status?: number } })?.response?.status === 403 ? 'forbidden' : 'error');

/** Το μήνυμα του server (error ή message), αλλιώς το εφεδρικό. */
export const serverMessage = (err: unknown, fallback: string): string => {
  const data = (err as { response?: { data?: { error?: unknown; message?: unknown } } })?.response?.data;
  const msg = data?.error || data?.message;
  return typeof msg === 'string' && msg.trim() ? msg : fallback;
};

/* ───────── αναλυτικά (GET /organizations/:id/analytics) ───────── */

export interface OrgKpis {
  revenue?: number;
  revenue_prev?: number;
  orders?: number;
  orders_prev?: number;
  aov?: number;
  aov_prev?: number;
  customers?: number;
  customers_prev?: number;
  items?: number;
  tips?: number;
  cancelled_orders?: number;
  cancel_rate?: number;
  pending_approvals?: number;
}

export interface OrgAnalytics {
  range?: { from: string; to: string; days: number };
  kpis?: OrgKpis;
  timeseries?: Array<{ date: string; orders: number; revenue: number }>;
  by_channel?: Array<{ channel: string; orders: number; revenue: number }>;
  by_hour?: Array<{ hour: number; orders: number; revenue: number }>;
  by_dow?: Array<{ dow: number; orders: number; revenue: number }>;
  by_payment?: Array<{ method: string; count: number; amount: number }>;
  top_products?: Array<{ name: string; qty: number; revenue: number }>;
  top_categories?: Array<{ category: string; qty: number; revenue: number }>;
  by_store?: Array<{
    id: number; name: string; display_name?: string | null; city?: string | null; is_active: boolean;
    orders: number; revenue: number; revenue_prev: number; aov: number;
  }>;
  waste_by_store?: Array<{ id: number; store: string; entries: number; qty: number; value: number; last_entry: string | null }>;
  financial?: {
    gross?: number; net?: number; vat?: number; tips?: number; env_fees?: number; delivery_fees?: number;
    cancelled_orders?: number; cancelled_value?: number; vat_from_documents?: boolean;
  };
  catalog_health?: {
    products?: number; untemplatized_groups?: number; sizes_no_price?: number; price_lists?: number;
    option_templates?: number; size_templates?: number; pending_approvals?: number;
  };
}
